import { Analytics } from '@gik/analytics';
import { AnalyticsEvents } from '@gik/analytics/utils/Events';
import { useInkind } from '@gik/api/inkinds/inkind';
import { useProductsBySlug } from '@gik/api/products/productsBySlug';
import { CheckoutLauncher } from '@gik/checkout/components/CheckoutLauncher/CheckoutLauncher';
import { createCartItemFromProduct } from '@gik/checkout/utils/productUtils';
import VisaLogo from '@gik/core/assets/img/visaLogo.svg';
import type { CartItem } from '@gik/core/models/gik/Order';
import routes from '@gik/core/routes';
import { LayoutTypes } from '@gik/core/types/layouts';
import { useBemCN } from '@gik/core/utils/bemBlock';
import { translationKeys } from '@gik/inkind-page/i18n/en';
import { AnchorLink } from '@gik/ui/AnchorLink';
import { Button } from '@gik/ui/Button';
import { Heading } from '@gik/ui/Heading';
import { Wishlist } from '@gik/ui/SvgIcon/GikIcons/Wishlist';
import { SvgIcon } from '@gik/ui/SvgIcon/SvgIcon';
import { UI } from '@gik/ui/UIManager';
import CalendarIcon from '@heroicons/react/outline/CalendarIcon';
import ChatAlt2Icon from '@heroicons/react/outline/ChatAlt2Icon';
import CreditCardIcon from '@heroicons/react/outline/CreditCardIcon';
import GiftIcon from '@heroicons/react/outline/GiftIcon';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { getInkindPageViewHistory } from '../InkindPageViewRecorder/InkindPageViewRecorder';
import { openSendAGiftBoxModal } from '../SendAGiftBoxModal';

export interface ISupportButtonsProps {
  inkindRouteId?: string;
  isSamplePage?: boolean;
  visaBtn?: boolean;
  messagesBtn?: boolean;
  calendarBtn?: boolean;
  wishlistBtn?: boolean;
  giftBoxBtn?: boolean;
  careCardBtn?: boolean;
}

// TODO please remove inline styles and/or fonticon upon migrating this to SPA
export default function SupportButtons({
  inkindRouteId,
  isSamplePage,
  visaBtn,
  messagesBtn,
  calendarBtn,
  wishlistBtn,
  giftBoxBtn,
  careCardBtn,
}: ISupportButtonsProps) {
  const { t } = useTranslation();
  const inkindPageViewHistory = getInkindPageViewHistory();
  const { data: inkindPage } = useInkind(inkindRouteId);

  const [supportThroughGIK, setSupportThroughGIK] = React.useState<boolean>(false);

  React.useEffect(() => {
    setSupportThroughGIK(inkindPage?.wishList || inkindPage?.isCalendar || (inkindPage?.CTA && inkindPage?.GlobalCTA));
  }, [inkindPage]);

  const bem = useBemCN('support-buttons');

  const { data: careCardProducts } = useProductsBySlug({ slug: ['carefund-rewards'] });
  const { data: visaCardProducts } = useProductsBySlug({ slug: ['visa-gift-card'] });

  if (inkindRouteId && !supportThroughGIK) return null;

  const careCardProduct = careCardProducts?.[0];
  const visaCardProduct = visaCardProducts?.[0];

  const cartItem: CartItem = careCardProduct ? createCartItemFromProduct(careCardProduct, inkindPage, true) : undefined;
  const visaCartItem: CartItem = visaCardProduct
    ? createCartItemFromProduct(visaCardProduct, inkindPage, true)
    : undefined;

  const hasVisaBtn = inkindRouteId === undefined ? visaBtn : inkindPage?.allowVisaCard && !!visaCardProduct;

  const hasMessagesBtn = inkindRouteId === undefined ? messagesBtn : inkindPage?.allowDiscussions;
  const hasCareCardBtn = inkindRouteId === undefined ? calendarBtn : inkindPage?.allowCareCard && careCardProduct;
  const hasCalendarBtn = inkindRouteId === undefined ? wishlistBtn : inkindPage?.isCalendar;
  const hasWishlistBtn = inkindRouteId === undefined ? giftBoxBtn : inkindPage?.wishList;
  const hasGiftBoxBtn = inkindRouteId === undefined ? careCardBtn : inkindPage?.CTA && inkindPage?.GlobalCTA;

  let enabledButtonsCount = 0;
  if (hasMessagesBtn) enabledButtonsCount++;
  if (hasCalendarBtn) enabledButtonsCount++;
  if (hasWishlistBtn) enabledButtonsCount++;
  if (hasGiftBoxBtn) enabledButtonsCount++;
  if (hasCareCardBtn) enabledButtonsCount++;

  return (
    <div {...bem(null, [{ [enabledButtonsCount & 1 ? 'odd' : 'even']: enabledButtonsCount }])}>
      <div {...bem('heading-wrapper')}>
        <Heading level={2} {...bem('h2', ['no-uppercase'])}>
          Quick Links to Help
        </Heading>
      </div>

      {/* Visa button */}
      {hasVisaBtn && (
        <CheckoutLauncher
          key={visaCardProduct?.id}
          initiatedOn="inkindPage"
          cart={[visaCartItem]}
          cachedProducts={[visaCardProduct]}
          initialInkindRouteId={inkindRouteId}
          child={onClick => (
            <Button
              fullWidth
              type="button"
              {...bem('btn-visa')}
              uppercase={false}
              variant="visa-gradient"
              layout={LayoutTypes.vertical}
              onClick={ev => {
                if (isSamplePage) return void UI.notifyError("Visa Cards can't be purchased for a sample page");

                Analytics.fireEvent(AnalyticsEvents.VisaCTAClicked, {
                  inkindRouteId,
                });

                onClick(ev);
              }}
            >
              <div className="tw-flex tw-items-center tw-gap-2">
                <VisaLogo style={{ width: '60px' }} />
                {t(translationKeys.VisaButton)}
              </div>
            </Button>
          )}
        />
      )}

      <div {...bem('buttons')}>
        {/* Discussion button */}
        {hasMessagesBtn && (
          <AnchorLink to="inkind-messages" offset={-80} {...bem('anchor')}>
            <Button
              type="button"
              {...bem('btn-messages')}
              uppercase={false}
              variant="info-gradient"
              prepend={<SvgIcon Icon={ChatAlt2Icon} />}
              layout={LayoutTypes.vertical}
            >
              {t(translationKeys.messages)}
            </Button>
          </AnchorLink>
        )}

        {/* Calendar button */}
        {hasCalendarBtn && (
          <AnchorLink to="inkind-calendar" offset={-80} {...bem('anchor')}>
            <Button
              type="button"
              {...bem('from-calendar')}
              uppercase={false}
              variant="aqua-gradient"
              prepend={<SvgIcon Icon={CalendarIcon} />}
              layout={LayoutTypes.vertical}
            >
              {t(translationKeys.fromCalendar)}
            </Button>
          </AnchorLink>
        )}

        {/* Wishlist button */}
        {hasWishlistBtn && (
          <Button
            type={'a'}
            href={`${routes.inkinds}/${inkindPage?.routeId}/wishlist`}
            {...bem('from-wishlist')}
            uppercase={false}
            variant="danger-gradient"
            prepend={<SvgIcon Icon={Wishlist} />}
            layout={LayoutTypes.vertical}
          >
            {t(translationKeys.fromWishlist).toString()}
          </Button>
        )}

        {/* Gift Box button */}
        {hasGiftBoxBtn && (
          <Button
            {...bem('send-giftbox')}
            uppercase={false}
            variant="purple-gradient"
            prepend={<SvgIcon Icon={GiftIcon} />}
            onClick={() => {
              if (isSamplePage) return UI.notifyError("Gift Boxes can't be purchased for a sample page");
              openSendAGiftBoxModal(inkindPage?.routeId, inkindPageViewHistory);
            }}
            layout={LayoutTypes.vertical}
          >
            {t(translationKeys.sendAGiftBox)}
          </Button>
        )}

        {/* Care Card button */}
        {hasCareCardBtn && (
          <CheckoutLauncher
            key={careCardProduct?.id}
            initiatedOn="inkindPage"
            cart={[cartItem]}
            cachedProducts={[careCardProduct]}
            initialInkindRouteId={inkindRouteId}
            child={onClick => (
              <Button
                {...bem('btn-carecard')}
                uppercase={false}
                fullWidth
                variant="success-gradient"
                prepend={<SvgIcon Icon={CreditCardIcon} />}
                onClick={ev => {
                  if (isSamplePage) return UI.notifyError("Care Cards can't be purchased for a sample page");
                  onClick(ev);
                }}
                layout={LayoutTypes.vertical}
              >
                {t(translationKeys.careCard)}
              </Button>
            )}
          />
        )}
      </div>
    </div>
  );
}
